import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Container, Typography, Paper, Grid, TextField, Button, 
    FormControl, InputLabel, Select, MenuItem, Divider, Stack,
    Card, CardContent, InputAdornment, LinearProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import {
    Save as SaveIcon,
    Settings as SettingsIcon,
    Class as ClassIcon,
    AttachMoney as MoneyIcon,
    AddCircle as AddIcon,
    Info as InfoIcon,
    Badge as BadgeIcon,
    Delete as DeleteIcon,
    PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';
import { getAllSclasses } from '../../../redux/sclassRelated/sclassHandle';
import { useToast } from '../../../context/ToastContext';
import useReceiptConfig from '../../../hooks/useReceiptConfig';
import axios from 'axios';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const FeeSetup = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);
    const { sclassesList } = useSelector((state) => state.sclass);
    const { showToast } = useToast();

    const [selectedClass, setSelectedClass] = useState("");
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [openConfig, setOpenConfig] = useState(false);
    const [newFieldName, setNewFieldName] = useState('');

    const [receiptConfig, updateReceiptConfig] = useReceiptConfig({
        schoolName: currentUser?.schoolName || '',
        email: currentUser?.email || ''
    });

    const [setupData, setSetupData] = useState({
        feeDetails: {},
        customFields: []
    });

    const baseFeeLabels = {
        tuition: "Tuition Fee",
        admission: "Admission Fee",
        annual: "Annual Fee",
        security: "Security Deposit",
        examination: "Examination Fee",
        computer: "Computer Fee",
        transportation: "Transportation Fee",
        hostel: "Hostel Fee",
        stationary: "Stationary Fee",
        sports: "Sports Fee",
        transferCertificate: "Transfer Certificate",
        characterCertificate: "Character Certificate",
        specialTraining: "Special Training",
        meal: "Meal Fee",
        educationalTour: "Educational Tour",
        eca: "ECA Fee",
        educationalMaterial: "Educational Material",
        extras: "Extras"
    };

    const customFeeLabels = (receiptConfig.customFields || []).reduce((acc, field) => {
        acc[field.id] = field.label;
        return acc;
    }, {});

    const feeLabels = { ...baseFeeLabels, ...customFeeLabels };

    useEffect(() => {
        if (currentUser) {
            dispatch(getAllSclasses(currentUser._id, "Sclass"));
        }
    }, [currentUser, dispatch]);

    useEffect(() => {
        if (selectedClass) {
            fetchClassSetup();
        } else {
            resetSetup();
        }
    }, [selectedClass]);

    const resetSetup = () => {
        setSetupData({
            feeDetails: Object.keys(feeLabels).reduce((acc, key) => ({ ...acc, [key]: '' }), {}),
            customFields: []
        });
    };

    const fetchClassSetup = async () => {
        setFetching(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/FeeSetup/${currentUser._id}/${selectedClass}`);
            if (response.data) {
                const setup = response.data.feeDetails || {};
                const customFieldsFromDb = response.data.customFields || [];
                const customAmounts = {};
                customFieldsFromDb.forEach(field => {
                    customAmounts[field.id] = field.amount;
                });

                const newDetails = {};
                Object.keys(feeLabels).forEach(key => {
                    const val = baseFeeLabels[key] ? setup[key] : customAmounts[key];
                    newDetails[key] = (val !== undefined && val !== null && val !== 0) ? val.toString() : '';
                });
                setSetupData({
                    feeDetails: newDetails,
                    customFields: customFieldsFromDb
                });
            } else {
                resetSetup();
            }
        } catch (error) {
            console.error("No setup found for this class");
            resetSetup();
        } finally {
            setFetching(false);
        }
    };

    const handleAmountChange = (category, value) => {
        setSetupData(prev => ({
            ...prev,
            feeDetails: {
                ...prev.feeDetails,
                [category]: value
            }
        }));
    };

    const handleSave = async () => {
        if (!selectedClass) {
            showToast("Please select a class first", "warning");
            return;
        }

        // Convert strings back to numbers for saving and map custom fields
        const formattedFeeDetails = {};
        const formattedCustomFields = [];

        Object.keys(setupData.feeDetails).forEach(key => {
            const amount = parseFloat(setupData.feeDetails[key]) || 0;
            if (baseFeeLabels[key]) {
                formattedFeeDetails[key] = amount;
            } else if (customFeeLabels[key]) {
                formattedCustomFields.push({
                    id: key,
                    label: customFeeLabels[key],
                    amount: amount
                });
            }
        });

        setLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_BASE_URL}/FeeSetup`, {
                sclassName: selectedClass,
                school: currentUser._id,
                feeDetails: formattedFeeDetails,
                customFields: formattedCustomFields
            });
            showToast("Fee configuration saved successfully", "success");
        } catch (error) {
            showToast("Error saving configuration", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageContainer>
            <HeaderWrapper elevation={0}>
                <Container maxWidth="xl">
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 4 }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', mb: 1 }}>
                                Fee Setup
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Configure standard fee structures and receipt templates
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                startIcon={<SettingsIcon />}
                                onClick={() => setOpenConfig(true)}
                                sx={{ 
                                    bgcolor: 'rgba(255,255,255,0.1)', 
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 700
                                }}
                            >
                                Receipt Template
                            </Button>
                         </Stack>
                    </Stack>
                </Container>
            </HeaderWrapper>

            <Dialog open={openConfig} onClose={() => setOpenConfig(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Configure Receipt Template</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="subtitle2" color="primary" fontWeight="700">School Header Information</Typography>
                        <TextField
                            label="School Name"
                            fullWidth
                            value={receiptConfig.header.schoolName}
                            onChange={(e) => updateReceiptConfig({ header: { schoolName: e.target.value } })}
                        />
                        <TextField
                            label="Address"
                            fullWidth
                            value={receiptConfig.header.address}
                            onChange={(e) => updateReceiptConfig({ header: { address: e.target.value } })}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Contact Number"
                                    fullWidth
                                    value={receiptConfig.header.contact}
                                    onChange={(e) => updateReceiptConfig({ header: { contact: e.target.value } })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Email"
                                    fullWidth
                                    value={receiptConfig.header.email}
                                    onChange={(e) => updateReceiptConfig({ header: { email: e.target.value } })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="PAN Number"
                                    fullWidth
                                    placeholder="Enter School PAN Number"
                                    value={receiptConfig.header.panNumber}
                                    onChange={(e) => updateReceiptConfig({ header: { panNumber: e.target.value } })}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><BadgeIcon sx={{ color: '#667eea', fontSize: 20 }} /></InputAdornment>,
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 1 }} />
                        
                        <Typography variant="subtitle2" color="primary" fontWeight="700">Fields to Show in Receipt</Typography>
                        <Grid container spacing={1}>
                            {(receiptConfig.customFields || []).map((field) => (
                                <Grid item xs={12} key={field.id}>
                                    <Paper sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f8fafc', borderRadius: '10px' }}>
                                        <Typography variant="body2" fontWeight="600">{field.label}</Typography>
                                        <IconButton size="small" color="error" onClick={() => {
                                            const filteredFields = (receiptConfig.customFields || []).filter(f => f.id !== field.id);
                                            updateReceiptConfig({ customFields: filteredFields });
                                        }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                        
                        <Stack direction="row" spacing={1}>
                            <TextField
                                label="Add New Fee Category"
                                size="small"
                                fullWidth
                                value={newFieldName}
                                onChange={(e) => setNewFieldName(e.target.value)}
                            />
                            <Button 
                                variant="contained" 
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    if (!newFieldName.trim()) return;
                                    const newId = 'custom_' + Date.now();
                                    const newField = { id: newId, label: newFieldName.trim() };
                                    updateReceiptConfig({
                                        customFields: [...(receiptConfig.customFields || []), newField],
                                        showFields: { ...receiptConfig.showFields, [newId]: true }
                                    });
                                    setNewFieldName('');
                                }}
                                sx={{ textTransform: 'none' }}
                            >
                                Add
                            </Button>
                        </Stack>

                        <Divider sx={{ my: 1 }} />
                        
                        <Typography variant="subtitle2" color="primary" fontWeight="700">Receipt Footer</Typography>
                        <TextField
                            label="Footer Text"
                            fullWidth
                            multiline
                            rows={2}
                            value={receiptConfig.footer}
                            onChange={(e) => updateReceiptConfig({ footer: e.target.value })}
                        />

                        <Divider sx={{ my: 1 }} />

                        <Typography variant="subtitle2" color="primary" fontWeight="700">Logo</Typography>
                        <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="logo-upload"
                            type="file"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    if (file.size > 200 * 1024) {
                                        showToast("Logo file too large. Please use an image smaller than 200KB.", "warning");
                                        return;
                                    }
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                        updateReceiptConfig({ header: { logo: reader.result } });
                                    };
                                    reader.readAsDataURL(file);
                                }
                            }}
                        />
                        <label htmlFor="logo-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<PhotoCameraIcon />}
                                fullWidth
                                sx={{ borderRadius: '10px' }}
                            >
                                {receiptConfig.header.logo ? "Change Logo" : "Upload Logo"}
                            </Button>
                        </label>
                        {receiptConfig.header.logo && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: '#f8fafc', borderRadius: '10px' }}>
                                <img src={receiptConfig.header.logo} alt="School Logo" style={{ maxHeight: '80px', maxWidth: '100%' }} />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfig(false)} variant="contained" sx={{ borderRadius: '10px' }}>Done</Button>
                </DialogActions>
            </Dialog>

            <Container maxWidth="xl" sx={{ mt: 2, height: 'calc(100vh - 120px)', overflow: 'hidden', pb: 2 }}>
                <Grid container spacing={3} sx={{ height: '100%' }}>
                    <Grid item xs={12} md={4} sx={{ height: '100%' }}>
                        <Box sx={{ height: '100%' }}>
                            <StyledPaper elevation={3}>
                            <SectionHeader>
                                <ClassIcon />
                                <Typography variant="h6" fontWeight="700">Classes</Typography>
                            </SectionHeader>
                            <Box sx={{ p: 2, flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '10px' } }}>                                
                                <Stack spacing={1}>
                                    {sclassesList && sclassesList.map((sclass) => (
                                        <ClassItem
                                            key={sclass._id}
                                            $active={selectedClass === sclass._id}
                                            onClick={() => setSelectedClass(sclass._id)}
                                            startIcon={<ClassIcon />}
                                        >
                                            {sclass.sclassName}
                                        </ClassItem>
                                    ))}
                                    {(!sclassesList || sclassesList.length === 0) && (
                                        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                                            No classes found.
                                        </Typography>
                                    )}
                                </Stack>

                                <Box sx={{ mt: 2 }}>
                                    <Alert icon={<InfoIcon fontSize="small" />} severity="info" sx={{ borderRadius: '12px', fontSize: '0.75rem', py: 0 }}>
                                        Select a class to configure its standard fee amounts.
                                    </Alert>
                                </Box>
                            </Box>
                        </StyledPaper>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={8} sx={{ height: '100%', overflowY: 'auto', pr: 1, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '10px' } }}>
                        <StyledPaper elevation={3}>
                            <SectionHeader>
                                <MoneyIcon />
                                <Typography variant="h6" fontWeight="700">Fee Details {selectedClass && `( ${sclassesList.find(c => c._id === selectedClass)?.sclassName} )`}</Typography>
                            </SectionHeader>
                            
                            <Box sx={{ p: 3 }}>
                                {!selectedClass ? (
                                    <Box sx={{ py: 10, textAlign: 'center' }}>
                                        <Typography color="textSecondary">Please select a class to configure fees.</Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <Grid container spacing={2}>
                                            {Object.entries(feeLabels).map(([key, label]) => (
                                                <Grid item xs={12} sm={6} md={4} key={key}>
                                                    <TextField
                                                        fullWidth
                                                        label={label}
                                                        type="number"
                                                        value={setupData.feeDetails[key]}
                                                        onChange={(e) => handleAmountChange(key, e.target.value)}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start">Rs.</InputAdornment>,
                                                        }}
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>

                                        <Divider sx={{ my: 4 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={resetSetup}
                                                sx={{ borderRadius: '12px', px: 4, textTransform: 'none', fontWeight: 700 }}
                                            >
                                                Reset
                                            </Button>
                                            <Button
                                                variant="contained"
                                                startIcon={<SaveIcon />}
                                                onClick={handleSave}
                                                disabled={loading}
                                                sx={{ 
                                                    borderRadius: '12px', 
                                                    px: 6, 
                                                    py: 1.5,
                                                    bgcolor: '#7f56da',
                                                    '&:hover': { bgcolor: '#6b44bc' },
                                                    textTransform: 'none', 
                                                    fontWeight: 700 
                                                }}
                                            >
                                                {loading ? 'Saving...' : 'Save Configuration'}
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </StyledPaper>
                    </Grid>
                </Grid>
            </Container>
        </PageContainer>
    );
};

export default FeeSetup;

const PageContainer = styled.div`background: #f8fafc; min-height: 100vh; animation: ${fadeIn} 0.5s ease-out;`;
const HeaderWrapper = styled(Paper)`
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    border-radius: 0 0 40px 40px !important;
    padding-bottom: 60px;
`;
const StyledPaper = styled(Paper)`
    border-radius: 20px !important;
    overflow: hidden;
    height: 100%;
    border: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
`;
const SectionHeader = styled(Box)`
    background: #f8fafc;
    padding: 16px 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 12px;
    color: #1e293b;
    svg { color: #7f56da; }
`;

const ClassItem = styled(Button)`
    width: 100%;
    justify-content: flex-start !important;
    text-align: left !important;
    padding: 12px 16px !important;
    margin-bottom: 8px !important;
    border-radius: 12px !important;
    text-transform: none !important;
    font-weight: 600 !important;
    color: ${props => props.$active ? '#fff' : '#64748b'} !important;
    background: ${props => props.$active ? 'linear-gradient(135deg, #7f56da 0%, #6b44bc 100%)' : 'transparent'} !important;
    border: 1px solid ${props => props.$active ? 'transparent' : '#e2e8f0'} !important;
    box-shadow: ${props => props.$active ? '0 4px 12px rgba(127, 86, 218, 0.3)' : 'none'} !important;
    transition: all 0.2s ease !important;

    &:hover {
        background: ${props => props.$active ? 'linear-gradient(135deg, #7f56da 0%, #6b44bc 100%)' : '#f1f5f9'} !important;
        transform: translateX(4px);
    }

    svg {
        margin-right: 12px;
        color: ${props => props.$active ? '#fff' : '#7f56da'};
    }
`;
